import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { getAllPosts, getSettings } from './blogService';
import { samplePosts } from './sampleData';
import { format } from 'date-fns';
import { ArrowDown } from 'lucide-react';
import Admin from './Admin';
import './App.css';

// Home Page Component
const HomePage = ({ onOpenAdmin }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState('/afg-logo.svg');

  useEffect(() => {
    loadPosts();
    getSettings().then((settings) => {
      if (settings.logo_url) setLogoUrl(settings.logo_url);
    });
  }, []);

  const loadPosts = async () => {
    try {
      // Fetch posts in batches. Render each batch as it arrives so the page
      // appears quickly and we never exceed the serverless 6MB response limit.
      const data = await getAllPosts((batch) => {
        if (batch.length > 0) {
          setPosts(batch);
          setLoading(false);
        }
      });
      // If no posts from database, fall back to sample posts
      setPosts(data.length > 0 ? data : samplePosts);
    } catch (error) {
      console.error('Error loading posts:', error);
      // On error, show sample posts
      setPosts(samplePosts);
    } finally {
      setLoading(false);
    }
  };

  const scrollToFooter = () => {
    const footer = document.getElementById('footer');
    if (footer) {
      footer.scrollIntoView({ behavior: 'auto' });
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <>
      {/* Live Webcam Background */}
      <div className="live-webcam">
        <iframe
          src="https://www.youtube.com/embed/F0GOOP82094?autoplay=1&mute=1&controls=0&loop=1&playlist=F0GOOP82094&modestbranding=1&showinfo=0&rel=0"
          title="Live Stream"
          frameBorder="0"
          allow="autoplay; encrypted-media"
          allowFullScreen
        ></iframe>
      </div>

      {/* Logo - Double click to open admin */}
      <Link to="/" className="logo" onDoubleClick={onOpenAdmin}>
        <img src={logoUrl} alt="afg.fm" />
      </Link>

      {/* Scroll Button */}
      <button className="scroll-button" onClick={scrollToFooter}>
        <ArrowDown size={24} />
      </button>

      {/* Content */}
      <div className="content">
        {posts.length === 0 ? (
          <div className="no-posts">
            <h2>Welcome to afg.fm</h2>
            <p>No notes yet.</p>
          </div>
        ) : (
          posts.map((post) => (
            <article key={post.id} className="post">
              {post.featured_image && (
                <img src={post.featured_image} alt={post.title} className="post-image" />
              )}
              <h2>{post.title}</h2>
              <div dangerouslySetInnerHTML={{ __html: post.content }} />
              {post.post_type === 'spotify' && post.spotify_embed && (
                <div dangerouslySetInnerHTML={{ __html: post.spotify_embed }} />
              )}
              {post.post_type === 'link' && post.link_url && (
                <a href={post.link_url} target="_blank" rel="noopener noreferrer" className="external-link">
                  Visit Link ↗
                </a>
              )}
              <time>{format(new Date(post.created_at), 'MMMM d, yyyy')}</time>
            </article>
          ))
        )}
      </div>
    </>
  );
};

// Terms Page Component
const TermsPage = () => {
  return (
    <div className="terms-page">
      <Link to="/" className="logo">
        <img src="/afg-logo.svg" alt="afg.fm" />
      </Link>
      <div className="terms-content">
        <Link to="/" className="back-link">← Back</Link>
        <h1>Terms & Conditions</h1>
        
        <p>Aesthetic Form: Goodwill ("AF:G") is a for-profit record label. Under its agreement with Goodwill Industries International, Inc. ("Goodwill"), AF:G has committed to pay Goodwill 15% of the Net Income it actually receives from the exploitation of specified recordings (including My Way, Believe It or Not, Happy Together, Save the Last Dance for Me, Can't Take My Eyes Off You), related products (digital streaming, physical formats such as CDs and vinyl), licensing (including synchronization licensing), and associated marketing and promotional activities (including promotion through Goodwill channels) during the period October 24, 2025 through October 24, 2026 in the United States.</p>
        
        <p>Goodwill Industries International, Inc. is a registered 501(c)(3) nonprofit organization. Goodwill works to enhance the quality of life of individuals and families by strengthening communities, building bridges to opportunity, and helping people in need reach their potential through learning and the Power of Work®.</p>
        
        <p>These contributions are made by AF:G from its own business revenues. Listeners, customers, or licensees are not making charitable donations by streaming or purchasing AF:G music. The purchase, streaming, or licensing of AF:G's music or other products and services are not tax-deductible charitable contributions by listeners, customers, or licensees.</p>
        
        <p>Goodwill does not sponsor, endorse, or administer AF:G's music, activities, events, or related operations and makes no representations regarding AF:G's operations, products, or services. AF:G is solely responsible for its business activities, marketing, and compliance with all applicable charitable sales promotion and commercial co-venture laws, and for conducting and administering this promotion.</p>
        
        <p>Goodwill Industries International, Inc. is the sole and exclusive owner of all rights in and to its trademarks, whether registered or not, including but not limited to GOODWILL®, GOODWILL INDUSTRIES®, and the smiling G logo (collectively, the "GII Trademarks"). Any use of the GII Trademarks by AF:G is pursuant to a limited license.</p>
        
        <p><strong>Contact:</strong> <a href="mailto:info@afg.fm">info@afg.fm</a></p>
      </div>
    </div>
  );
};

// Footer Component
const Footer = () => {
  const location = useLocation();
  if (location.pathname !== '/') return null;

  return (
    <footer id="footer" className="footer">
      <a href="mailto:info@afg.fm">CONTACT</a>
      <Link to="/terms">TERMS</Link>
    </footer>
  );
};

function App() {
  const [showAdmin, setShowAdmin] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);

  const ADMIN_PASSWORD = 'admin123'; // Change this!

  // Keyboard shortcut: Cmd+Shift+A
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setShowPasswordPrompt(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handlePasswordSubmit = () => {
    if (password === ADMIN_PASSWORD) {
      setShowAdmin(true);
      setShowPasswordPrompt(false);
      setPassword('');
      setPasswordError(false);
    } else {
      setPasswordError(true);
    }
  };

  const openAdminPrompt = () => {
    setShowPasswordPrompt(true);
  };

  return (
    <Router>
      <div className="app">
        <Routes>
          <Route path="/" element={<HomePage onOpenAdmin={openAdminPrompt} />} />
          <Route path="/terms" element={<TermsPage />} />
        </Routes>
        <Footer />
        
        {/* Password Prompt */}
        {showPasswordPrompt && (
          <div className="password-overlay">
            <div className="password-prompt">
              <h3>Admin Access</h3>
              <input
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                autoFocus
              />
              {passwordError && <p className="error">Incorrect password</p>}
              <div className="password-actions">
                <button onClick={() => {
                  setShowPasswordPrompt(false);
                  setPassword('');
                  setPasswordError(false);
                }}>Cancel</button>
                <button onClick={handlePasswordSubmit}>Submit</button>
              </div>
            </div>
          </div>
        )}
        
        {/* Admin Panel */}
        {showAdmin && <Admin onClose={() => setShowAdmin(false)} />}
      </div>
    </Router>
  );
}

export default App;
